import React from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';

import {stringFormat} from '@ey/utils';

import {labels} from '../../../../util/utils';
import Utility from '../../../../util/utilityfunctions';

import OutputText from '@ey/canvascoreservices/OutputText';

import StyledBarChart from './StyledBarChart';
import Legend from '../Legend';

const BarChart = ({series, categories, title, onGraphicClick}) => {
	const numberFormat = useSelector(({userPreferences}) => userPreferences.numberFormat);

	const optionsBar = {
		chart: {
			type: 'column',
			backgroundColor: 'transparent'
		},
		credits: {enabled: false},
		title: {text: ''},
		subtitle: {text: ''},
		xAxis: {
			categories: categories,
			scrollbar: {enabled: false},
			visible: true,
			labels: {
				style: {width: 'var(--px-120)', textAlign: 'center'},
				useHTML: true,
				formatter(e) {
					let categoryLabel = e.value[0];
					let countTask = e.value[1] || 0;
					return `
						<span title="${categoryLabel}" class="motif-body2-default-regular two-line-ellipse">
							${categoryLabel}
						</span>
						<span class="motif-body2-default-light motif-body3-default-light">${Utility.getFormattedNumber(
							numberFormat,
							countTask
						)} ${labels.tasks.toLowerCase()}</span>
					`;
				}
			}
		},
		yAxis: {
			allowDecimals: false,
			min: 0,
			title: {text: title},
			visible: true,
			className: 'motif-body1-default-regular',
			labels: {
				useHTML: true,
				formatter(e) {
					return `<span class="motif-body2-default-regular">${e.value}</span>`;
				}
			}
		},
		plotOptions: {
			bar: {maxPointWidth: 20}, // column bar width
			series: {
				cursor: 'pointer',
				borderWidth: 2,
				borderColor: 'var(--neutrals-25)',
				pointWidth: 30,
				events: {
					mouseOver: function() {
						// what does this do??
						const gray = '#eaeaf2';
						const newGray = '#fafafc';

						this.options.oldColor = this.color;

						if (gray == this.color?.toLowerCase()) {
							this.graphic.attr('fill', newGray);
						}
					},
					mouseOut: function() {
						this.graphic && this.graphic.attr('fill', this.options.oldColor);
					}
				},
				point: {
					events: {
						click: (e) => {
							onGraphicClick && onGraphicClick(e);
						}
					}
				}
			},
			column: {
				stacking: 'normal',
				dataLabels: {
					enabled: false
				}
			}
		},
		tooltip: {
			useHTML: true,
			enabled: true,
			outside: true,
			followPointer: true,
			useHTML: true,
			formatter() {
				let countEngagement = this.point.y;
				let label =
					countEngagement === 1
						? labels.engagementCompletedBasedOnTaskCompletionMilestone
						: labels.engagementsCompletedBasedOnTaskCompletionMilestone;
				return `
					<div class="bar-tooltip" style="width: 20rem; white-space: initial">
						${stringFormat(label, Utility.getFormattedNumber(numberFormat, countEngagement), this.point['series'].name)}
					</div>
				`;
			}
		},
		legend: {enabled: false},
		series: series
	};

	const renderLegend = ({name}) => {
		return (
			<>
				<OutputText body3 light className="grey">
					{name}
				</OutputText>
			</>
		);
	};

	return (
		<StyledBarChart>
			<div className="bar-chart">
				<HighchartsReact highcharts={Highcharts} options={optionsBar} />
			</div>
			<Legend items={series} renderItem={renderLegend} title={labels.completionRange} />
		</StyledBarChart>
	);
};

BarChart.defaultProps = {
	onGraphicClick: () => {}
};

BarChart.propTypes = {
	categories: PropTypes.arrayOf(PropTypes.any),
	title: PropTypes.string,
	onGraphicClick: PropTypes.func,
	series: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string,
			data: PropTypes.arrayOf(PropTypes.any),
			color: PropTypes.string
		})
	)
};

export default BarChart;
